import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiRefreshCw, FiRotateCcw, FiExternalLink } from 'react-icons/fi';
import { DashLang } from '../../utils/dashboardTranslations';

interface Txn {
  id: number;
  gateway: string;
  channel: string | null;
  is_test?: boolean;
  status: string;
  amount: number;
  fee_amount: number;
  supplier_share: number | null;
  refunded_amount: number;
  refundable_amount: number;
  currency: string;
  invoice_id: string | null;
  payment_id: string | null;
  created_at: string;
  order: { id: number; order_number: string; customer_name: string } | null;
  refunds: { id: number; amount: number; status: string; initiated_by: string }[];
}

interface DocRow {
  file_type: number;
  label: string;
  label_ar: string;
  uploaded: boolean;
  id: number | null;
  original_name: string | null;
  size: number | null;
  status: string | null;
  reason: string | null;
  from_company_profile?: boolean;
}

interface Onboarding {
  stage: string;
  requires_admin_approval?: boolean;
  documents_status: string;
  review_reason: string | null;
  myfatoorah_reasons?: string[];
  iban: string | null;
  bank_id: number | null;
  bank_account: string | null;
  bank_account_holder: string | null;
  banks: { id: number; name: string }[];
  complete: boolean;
  documents: DocRow[];
  extra_documents: DocRow[];
  optional_types: { file_type: number; label: string; label_ar: string }[];
  stale_company_documents?: string[];
  company_documents_reused?: number;
  company_documents_fresh_days?: number;
}

interface Totals {
  count: number;
  gross: number;
  fees: number;
  refunded: number;
  net: number;
}

/**
 * The road from "I switched MyFatoorah on" to "cards work", drawn.
 *
 * Three parties act in turn and only the last one actually enables payments,
 * which is the thing sellers get wrong — so each step says who is doing it.
 * The flow is mirrored in Arabic so it reads right to left.
 */
function OnboardingFlow({ stage, lang, withOurReview }: { stage: string; lang: DashLang; withOurReview: boolean }) {
  const ar = lang === 'ar';
  const t = (en: string, arabic: string) => (ar ? arabic : en);

  // The review step only exists when a Hadaq admin actually checks the
  // documents. With that off they go straight to MyFatoorah, and showing a
  // step nobody performs would have the seller waiting on no one.
  const steps = [
    { key: 'collecting', title: t('Your documents', 'مستنداتك'), who: t('you', 'أنت'), note: t('Upload + IBAN', 'المستندات والآيبان') },
    ...(withOurReview
      ? [{ key: 'in_review', title: t('Our review', 'مراجعتنا'), who: t('Hadaq', 'حداق'), note: t('We check them', 'نتحقق منها') }]
      : []),
    { key: 'with_myfatoorah', title: t('Approval', 'الاعتماد'), who: 'MyFatoorah', note: t('They approve', 'يعتمدون الحساب') },
    { key: 'live', title: t('Cards live', 'الدفع مفعّل'), who: t('customers', 'عملاؤك'), note: t('Payments start', 'تبدأ المدفوعات') },
  ];

  // These all mean "the ball is back with the seller", including a refusal
  // from MyFatoorah — they are at the start again, not at the end.
  const normalised = ['ready_to_submit', 'changes_requested', 'myfatoorah_rejected'].includes(stage)
    ? 'collecting'
    : stage;
  const at = Math.max(0, steps.findIndex((s) => s.key === normalised));

  const W = 760;
  const H = 132;
  const boxH = 72;
  // Sized from the step count so three steps fill the width as four did.
  const gap = 32;
  const boxW = (W - gap * (steps.length - 1)) / steps.length;

  const colours = (i: number) => {
    if (i < at) return { fill: '#dcfce7', stroke: '#86efac', text: '#15803d' };
    if (i === at) return { fill: 'var(--primary, #2563eb)', stroke: 'var(--primary, #2563eb)', text: '#ffffff' };
    return { fill: '#f3f4f6', stroke: '#e5e7eb', text: '#9ca3af' };
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label={t('Onboarding steps', 'مراحل التفعيل')}
    >
      <defs>
        <marker id="mf-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#cbd5e1" />
        </marker>
      </defs>

      {steps.map((step, i) => {
        // Mirror the running order for Arabic so the flow reads right to left.
        const slot = ar ? steps.length - 1 - i : i;
        const x = slot * (boxW + gap);
        const c = colours(i);
        const done = i < at;

        return (
          <g key={step.key}>
            {i < steps.length - 1 && (
              <line
                x1={ar ? x : x + boxW}
                y1={boxH / 2 + 18}
                x2={ar ? x - gap + 8 : x + boxW + gap - 8}
                y2={boxH / 2 + 18}
                stroke="#cbd5e1"
                strokeWidth="2"
                markerEnd="url(#mf-arrow)"
              />
            )}

            <rect x={x} y={18} width={boxW} height={boxH} rx={14} fill={c.fill} stroke={c.stroke} strokeWidth="1.5" />

            <text x={x + boxW / 2} y={40} textAnchor="middle" fontSize="11" fill={c.text} opacity="0.85">
              {done ? '\u2713 ' : `${i + 1}. `}{step.who}
            </text>
            <text x={x + boxW / 2} y={58} textAnchor="middle" fontSize="14" fontWeight="600" fill={c.text}>
              {step.title}
            </text>
            <text x={x + boxW / 2} y={75} textAnchor="middle" fontSize="10.5" fill={c.text} opacity="0.8">
              {step.note}
            </text>

            {i === at && (
              <text x={x + boxW / 2} y={108} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--primary, #2563eb)">
                {t('you are here', 'أنت هنا')}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * The seller's own view of MyFatoorah: what their store took, what Hadaq
 * withheld, what MyFatoorah is holding for them, and the ability to refund a
 * customer. Balances come from MyFatoorah scoped to this store's supplier
 * account, so only this store's money is ever shown.
 */
export default function MyFatoorah() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const t = (en: string, ar: string) => (lang === 'ar' ? ar : en);

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('none');
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [iban, setIban] = useState('');
  const [holder, setHolder] = useState('');
  const [bankId, setBankId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [busyType, setBusyType] = useState<number | null>(null);
  const [savingBank, setSavingBank] = useState(false);
  const [submitting2, setSubmitting2] = useState(false);
  const [extraType, setExtraType] = useState('');
  const [totals, setTotals] = useState<Totals | null>(null);
  const [balance, setBalance] = useState<Record<string, any> | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [refunding, setRefunding] = useState<Txn | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundComment, setRefundComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currency = store?.currency_symbol || store?.currency || 'KWD';
  const money = useCallback(
    (n: number | null | undefined) => `${Number(n ?? 0).toFixed(3)} ${currency}`,
    [currency],
  );

  const load = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const [dash, list, docs] = await Promise.all([
        api.get(`/stores/${store.id}/myfatoorah/dashboard`),
        api.get(`/stores/${store.id}/myfatoorah/transactions`),
        api.get(`/stores/${store.id}/myfatoorah/documents`),
      ]);
      applyOnboarding(docs.data);
      setEnabled(Boolean(dash.data?.enabled));
      setStatusMessage(dash.data?.message ?? null);
      setStatus(dash.data?.status ?? 'none');
      setTotals(dash.data?.totals ?? null);
      setBalance(dash.data?.dashboard ?? null);
      setTxns(list.data?.data ?? []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Could not load payments', 'تعذّر تحميل المدفوعات'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  useEffect(() => { load(); }, [load]);

  function applyOnboarding(data: Onboarding) {
    setOnboarding(data);
    setIban(data.iban || '');
    setHolder(data.bank_account_holder || '');
    setBankId(data.bank_id ? String(data.bank_id) : '');
    setBankAccount(data.bank_account || '');
  }

  const uploadDoc = async (fileType: number, file: File) => {
    setBusyType(fileType);
    try {
      const body = new FormData();
      body.append('file_type', String(fileType));
      body.append('file', file);
      // The axios instance defaults to application/json, which would send the
      // multipart body with the wrong content type and lose the file. A 5 MB
      // document also needs more than the default 15s.
      const res = await api.post(`/stores/${store.id}/myfatoorah/documents`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      applyOnboarding(res.data);
      toast.success(t('Document uploaded', 'تم رفع المستند'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Upload failed', 'فشل الرفع'));
    } finally {
      setBusyType(null);
    }
  };

  const removeDoc = async (fileType: number, id: number) => {
    setBusyType(fileType);
    try {
      const res = await api.delete(`/stores/${store.id}/myfatoorah/documents/${id}`);
      applyOnboarding(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Could not remove it', 'تعذّر الحذف'));
    } finally {
      setBusyType(null);
    }
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      const res = await api.put(`/stores/${store.id}/myfatoorah/bank`, {
        iban,
        bank_account_holder: holder,
        bank_id: bankId ? Number(bankId) : undefined,
        bank_account: bankAccount || undefined,
      });
      applyOnboarding(res.data);
      toast.success(t('Bank details saved', 'تم حفظ بيانات الحساب'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Could not save', 'تعذّر الحفظ'));
    } finally {
      setSavingBank(false);
    }
  };

  const submitForReview = async () => {
    setSubmitting2(true);
    try {
      const res = await api.post(`/stores/${store.id}/myfatoorah/submit`);
      applyOnboarding(res.data);
      toast.success(t('Sent for review', 'تم الإرسال للمراجعة'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Could not submit', 'تعذّر الإرسال'));
    } finally {
      setSubmitting2(false);
    }
  };

  const openRefund = (tx: Txn) => {
    setRefunding(tx);
    setRefundAmount(tx.refundable_amount.toFixed(3));
    setRefundComment('');
  };

  const submitRefund = async () => {
    if (!refunding) return;
    const amount = Number(refundAmount);
    if (!(amount > 0) || amount > refunding.refundable_amount) {
      toast.error(t('Enter an amount up to the refundable total.', 'أدخل مبلغاً لا يتجاوز القابل للاسترجاع.'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/stores/${store.id}/myfatoorah/transactions/${refunding.id}/refund`, {
        amount,
        comment: refundComment || undefined,
      });
      toast.success(t('Refund submitted. MyFatoorah will confirm it shortly.', 'تم إرسال طلب الاسترجاع. ستؤكده ماي فاتورة قريباً.'));
      setRefunding(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Refund failed', 'فشل الاسترجاع'));
    } finally {
      setSubmitting(false);
    }
  };

  const cards = useMemo(() => ([
    { label: t('Payments received', 'المدفوعات المستلمة'), value: money(totals?.gross), sub: `${totals?.count ?? 0} ${t('payments', 'عملية')}` },
    { label: t('Hadaq fees', 'رسوم حداق'), value: money(totals?.fees) },
    { label: t('Refunded', 'المسترجع'), value: money(totals?.refunded) },
    { label: t('Your net', 'صافي حسابك'), value: money(totals?.net), highlight: true },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [totals, money, lang]);

  if (!store) return null;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1>{t('MyFatoorah', 'ماي فاتورة')}</h1>
        <button className="btn btn-sm btn-white" onClick={load} disabled={loading}>
          <FiRefreshCw size={14} style={{ marginInlineEnd: 6 }} />
          {t('Refresh', 'تحديث')}
        </button>
      </div>

      {loading && <div className="card" style={{ padding: 24 }}>{t('Loading…', 'جارٍ التحميل…')}</div>}

      {!loading && !enabled && (status === 'pending' || status === 'rejected') && onboarding && (
        <>
          <div className="card" style={{ padding: 20, marginBottom: 16, lineHeight: 1.8 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>{t('Getting your store ready for card payments', 'تجهيز متجرك لاستقبال المدفوعات')}</h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-500)' }}>
              {t(
                'Send us the documents below. We check them, then pass them to MyFatoorah, who approve the account before cards can be taken. We will email you at each step.',
                'أرسل لنا المستندات أدناه. نراجعها ثم نرسلها إلى ماي فاتورة التي تعتمد الحساب قبل تفعيل استقبال البطاقات. سنرسل لك بريداً عند كل خطوة.'
              )}
            </p>

            <div style={{ marginTop: 18 }}>
              <OnboardingFlow
                stage={onboarding.stage}
                lang={lang}
                withOurReview={Boolean(onboarding.requires_admin_approval)}
              />
            </div>
          </div>

          {onboarding.stage === 'in_review' && onboarding.requires_admin_approval && (
            <div className="card" style={{ padding: 18, marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
              <strong>{t('With us for review', 'قيد المراجعة لدينا')}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                {t('We are checking your documents. You will get an email as soon as they are passed to MyFatoorah.', 'نراجع مستنداتك الآن. سيصلك بريد فور إرسالها إلى ماي فاتورة.')}
              </p>
            </div>
          )}

          {onboarding.stage === 'with_myfatoorah' && (
            <div className="card" style={{ padding: 18, marginBottom: 16, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
              <strong>{t('With MyFatoorah', 'لدى ماي فاتورة')}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                {t('We accepted your documents and sent them on. MyFatoorah is reviewing the account — cards go live once they approve, and we will email you.', 'قبلنا مستنداتك وأرسلناها. ماي فاتورة تراجع الحساب الآن — يُفعّل الدفع فور موافقتهم وسيصلك بريد بذلك.')}
              </p>
            </div>
          )}

          {onboarding.stage === 'myfatoorah_rejected' && (
            <div className="card" style={{ padding: 18, marginBottom: 16, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
              <strong>{t('MyFatoorah did not approve the account', 'ماي فاتورة لم تعتمد الحساب')}</strong>
              {(onboarding.myfatoorah_reasons?.length ?? 0) > 0 ? (
                <ul style={{ margin: '8px 0 0', paddingInlineStart: 20, fontSize: 13 }}>
                  {onboarding.myfatoorah_reasons!.map((r) => <li key={r}>{r}</li>)}
                </ul>
              ) : (
                <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                  {t('No specific reason was given. Contact MyFatoorah for details.', 'لم تُذكر أسباب محددة. تواصل مع ماي فاتورة لمعرفة التفاصيل.')}
                </p>
              )}
              <p style={{ margin: '10px 0 0', fontSize: 13 }}>
                {t(
                  'Replace the documents named above using the list below, then send them again — MyFatoorah will review the new ones.',
                  'استبدل المستندات المذكورة أعلاه من القائمة أدناه ثم أعد الإرسال — وستراجع ماي فاتورة النسخ الجديدة.'
                )}
              </p>
            </div>
          )}

          {onboarding.stage === 'changes_requested' && onboarding.review_reason && (
            <div className="card" style={{ padding: 18, marginBottom: 16, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
              <strong>{t('We need a change', 'نحتاج تعديلاً')}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{onboarding.review_reason}</p>
            </div>
          )}

          {!!onboarding.company_documents_reused && (
            <div className="card" style={{ padding: 14, marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8', fontSize: 13 }}>
              {t(
                `We already had ${onboarding.company_documents_reused} of these from your company registration and attached them for you — just add the ones still missing.`,
                `لدينا ${onboarding.company_documents_reused} من هذه المستندات من تسجيل شركتك وتم إرفاقها تلقائياً — يكفي إضافة الباقي.`,
              )}
            </div>
          )}

          {!!onboarding.stale_company_documents?.length && (
            <div className="card" style={{ padding: 14, marginBottom: 16, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e', fontSize: 13 }}>
              {t(
                `These are on your company file but were uploaded more than ${onboarding.company_documents_fresh_days ?? 10} days ago, so please upload a current copy: `,
                `هذه المستندات موجودة في ملف شركتك لكن مضى على رفعها أكثر من ${onboarding.company_documents_fresh_days ?? 10} أيام، يرجى رفع نسخة حديثة: `,
              )}
              <strong>{onboarding.stale_company_documents.join('، ')}</strong>
            </div>
          )}

          <div className="card" style={{ padding: 0, marginBottom: 16 }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-100)', fontWeight: 600 }}>
              {t('Required documents', 'المستندات المطلوبة')}
            </div>
            {onboarding.documents.map((d) => (
              <div key={d.file_type} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px',
                borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap',
              }}>
                <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{lang === 'ar' ? d.label_ar : d.label}</div>
                  {d.uploaded && (
                    <div style={{ fontSize: 11, color: d.status === 'rejected' ? '#b91c1c' : 'var(--gray-400)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.status === 'rejected' ? (d.reason || t('Needs replacing', 'يحتاج استبدال')) : d.original_name}
                    </div>
                  )}
                </div>
                {d.uploaded && d.status !== 'rejected' && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#dcfce7', color: '#16a34a' }}>
                    {t('Received', 'مستلم')}
                  </span>
                )}
                {d.uploaded && d.status !== 'rejected' && d.from_company_profile && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#eff6ff', color: '#1d4ed8' }}>
                    {t('From your company file', 'من ملف شركتك')}
                  </span>
                )}
                {d.status === 'rejected' && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#fee2e2', color: '#b91c1c' }}>
                    {t('Replace', 'استبدل')}
                  </span>
                )}
                <label className="btn btn-sm btn-white" style={{ cursor: 'pointer', margin: 0 }}>
                  {busyType === d.file_type ? t('Working…', 'جارٍ…') : d.uploaded ? t('Replace', 'استبدال') : t('Upload', 'رفع')}
                  <input
                    type="file" hidden
                    accept=".jpg,.jpeg,.png,.bmp,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) uploadDoc(d.file_type, f);
                    }}
                  />
                </label>
                {d.uploaded && d.id && (
                  <button className="btn btn-sm btn-white" onClick={() => removeDoc(d.file_type, d.id!)}>
                    {t('Remove', 'حذف')}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{t('Anything else they asked for', 'مستندات إضافية')}</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--gray-500)' }}>
              {t(
                'Only if MyFatoorah asks your store for a document beyond the list above.',
                'فقط إذا طلبت ماي فاتورة من متجرك مستنداً غير الموجود بالقائمة أعلاه.'
              )}
            </p>

            {(onboarding.extra_documents ?? []).map((d) => (
              <div key={d.file_type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{lang === 'ar' ? d.label_ar : d.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.original_name}</div>
                </div>
                {d.id && (
                  <button className="btn btn-sm btn-white" onClick={() => removeDoc(d.file_type, d.id!)}>
                    {t('Remove', 'حذف')}
                  </button>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <select
                className="form-input"
                style={{ flex: '1 1 200px' }}
                value={extraType}
                onChange={(e) => setExtraType(e.target.value)}
              >
                <option value="">{t('Choose a document…', 'اختر مستنداً…')}</option>
                {(onboarding.optional_types ?? [])
                  .filter((o) => !(onboarding.extra_documents ?? []).some((d) => d.file_type === o.file_type))
                  .map((o) => (
                    <option key={o.file_type} value={o.file_type}>{lang === 'ar' ? o.label_ar : o.label}</option>
                  ))}
              </select>
              <label className="btn btn-white" style={{ cursor: extraType ? 'pointer' : 'not-allowed', margin: 0, opacity: extraType ? 1 : 0.5 }}>
                {t('Upload', 'رفع')}
                <input
                  type="file" hidden disabled={!extraType}
                  accept=".jpg,.jpeg,.png,.bmp,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f && extraType) { uploadDoc(Number(extraType), f); setExtraType(''); }
                  }}
                />
              </label>
            </div>
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{t('Bank account', 'الحساب البنكي')}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--gray-500)' }}>
              {t('Where MyFatoorah deposits your share of each sale.', 'الحساب الذي تُودع فيه ماي فاتورة حصتك من كل عملية بيع.')}
            </p>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ fontSize: 13 }}>{t('IBAN', 'رقم الآيبان')}</label>
              <input className="form-input" dir="ltr" placeholder="KW00XXXX0000000000000000000000"
                value={iban} onChange={(e) => setIban(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ fontSize: 13 }}>{t('Account holder name', 'اسم صاحب الحساب')}</label>
              <input className="form-input" value={holder} onChange={(e) => setHolder(e.target.value)} />
              <small style={{ color: 'var(--gray-400)', fontSize: 11 }}>
                {t('As it appears on the account, letters only.', 'كما يظهر في الحساب، حروف فقط بدون أرقام.')}
              </small>
            </div>

            {(onboarding.banks?.length ?? 0) > 0 && (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ fontSize: 13 }}>{t('Bank', 'البنك')}</label>
                <select className="form-input" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                  <option value="">{t('Choose your bank…', 'اختر البنك…')}</option>
                  {onboarding.banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label" style={{ fontSize: 13 }}>
                {t('Account number (optional)', 'رقم الحساب (اختياري)')}
              </label>
              <input
                className="form-input" dir="ltr" inputMode="numeric"
                value={bankAccount} onChange={(e) => setBankAccount(e.target.value)}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={saveBank}
              disabled={savingBank || !iban.trim() || !holder.trim() || ((onboarding.banks?.length ?? 0) > 0 && !bankId)}
            >
              {savingBank ? t('Saving…', 'جارٍ الحفظ…') : t('Save bank details', 'حفظ بيانات الحساب')}
            </button>
          </div>

          {(onboarding.stage === 'collecting' || onboarding.stage === 'ready_to_submit' || onboarding.stage === 'changes_requested' || onboarding.stage === 'myfatoorah_rejected') && (
            <div className="card" style={{ padding: 18, textAlign: 'center' }}>
              <button className="btn btn-primary" onClick={submitForReview} disabled={submitting2 || !onboarding.complete}>
                {submitting2 ? t('Sending…', 'جارٍ الإرسال…') : t('Send for review', 'إرسال للمراجعة')}
              </button>
              {!onboarding.complete && (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--gray-500)' }}>
                  {t('Add every document and your IBAN first.', 'أضف جميع المستندات ورقم الآيبان أولاً.')}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !enabled && status !== 'pending' && status !== 'rejected' && (
        <div className="card" style={{ padding: 24, lineHeight: 1.8 }}>
          <p style={{ margin: 0 }}>
            {t(
              'MyFatoorah is not active for this store yet. Turn it on from Payment Gateways to start taking card payments through Hadaq.',
              'ماي فاتورة غير مفعّلة لهذا المتجر بعد. فعّلها من صفحة بوابات الدفع لبدء استقبال المدفوعات عبر حداق.',
            )}
          </p>
          {statusMessage && <p style={{ margin: '10px 0 0', color: '#b91c1c' }}>{statusMessage}</p>}
          <a className="btn btn-primary" href="/dashboard/payment-gateways" style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t('Go to Payment Gateways', 'الذهاب إلى بوابات الدفع')} <FiExternalLink size={14} />
          </a>
        </div>
      )}

      {!loading && enabled && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            {cards.map((c) => (
              <div key={c.label} className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{c.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6, color: c.highlight ? 'var(--primary, #2563eb)' : undefined }}>
                  {c.value}
                </div>
                {c.sub && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{c.sub}</div>}
              </div>
            ))}
          </div>

          {balance && (
            <div className="card" style={{ padding: 16, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>{t('Your MyFatoorah balance', 'رصيدك لدى ماي فاتورة')}</h3>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
                {Object.entries(balance)
                  .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
                  .map(([k, v]) => (
                    <div key={k}>
                      <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{k.replace(/([a-z])([A-Z])/g, '$1 $2')}</div>
                      <div style={{ fontWeight: 600 }}>{String(v)}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
              <thead>
                <tr style={{ textAlign: lang === 'ar' ? 'right' : 'left', background: 'var(--gray-50)' }}>
                  <th style={{ padding: 12 }}>{t('Order', 'الطلب')}</th>
                  <th style={{ padding: 12 }}>{t('Date', 'التاريخ')}</th>
                  <th style={{ padding: 12 }}>{t('Amount', 'المبلغ')}</th>
                  <th style={{ padding: 12 }}>{t('Hadaq fee', 'رسوم حداق')}</th>
                  <th style={{ padding: 12 }}>{t('Your share', 'حصتك')}</th>
                  <th style={{ padding: 12 }}>{t('Status', 'الحالة')}</th>
                  <th style={{ padding: 12 }} />
                </tr>
              </thead>
              <tbody>
                {txns.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>
                    {t('No payments yet.', 'لا توجد مدفوعات بعد.')}
                  </td></tr>
                )}
                {txns.map((tx) => (
                  <tr key={tx.id} style={{ borderTop: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>
                        {tx.order?.order_number ?? '—'}
                        {tx.is_test && (
                          <span style={{ marginInlineStart: 6, fontSize: 10, padding: '1px 6px', borderRadius: 50, background: '#fef3c7', color: '#92400e' }}>
                            {t('TEST', 'تجريبي')}
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--gray-400)', fontSize: 11 }}>{tx.order?.customer_name}</div>
                    </td>
                    <td style={{ padding: 12, whiteSpace: 'nowrap' }}>
                      {new Date(tx.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-GB')}
                    </td>
                    <td style={{ padding: 12, whiteSpace: 'nowrap' }}>{money(tx.amount)}</td>
                    <td style={{ padding: 12, whiteSpace: 'nowrap', color: 'var(--gray-500)' }}>{money(tx.fee_amount)}</td>
                    <td style={{ padding: 12, whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {tx.supplier_share !== null ? money(tx.supplier_share) : money(tx.amount)}
                    </td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 50,
                        background: tx.status === 'success' ? '#dcfce7' : tx.status === 'pending' ? '#fef3c7' : '#fee2e2',
                        color: tx.status === 'success' ? '#16a34a' : tx.status === 'pending' ? '#92400e' : '#b91c1c',
                      }}>
                        {tx.status === 'success' ? t('Paid', 'مدفوع') : tx.status === 'pending' ? t('Pending', 'قيد الانتظار') : t('Failed', 'فشل')}
                      </span>
                      {Number(tx.refunded_amount) > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>
                          {t('Refunded', 'مسترجع')} {money(tx.refunded_amount)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12, textAlign: 'end' }}>
                      {tx.status === 'success' && tx.refundable_amount > 0 && (
                        <button className="btn btn-sm btn-white" onClick={() => openRefund(tx)}>
                          <FiRotateCcw size={13} style={{ marginInlineEnd: 5 }} />
                          {t('Refund', 'استرجاع')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {refunding && (
        <div
          onClick={() => !submitting && setRefunding(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000,
          }}
        >
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: 22, width: '100%', maxWidth: 400 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>{t('Refund payment', 'استرجاع مبلغ')}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6 }}>
              {t('Order', 'الطلب')} {refunding.order?.order_number} — {t('up to', 'حتى')} {money(refunding.refundable_amount)}.
              <br />
              {t('The amount comes out of your own balance; the Hadaq fee is not returned.', 'يُخصم المبلغ من رصيدك، ولا تُسترجع رسوم حداق.')}
            </p>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ fontSize: 13 }}>{t('Amount', 'المبلغ')}</label>
              <input
                className="form-input" type="number" step="0.001" min="0.001"
                max={refunding.refundable_amount} dir="ltr"
                value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 18 }}>
              <label className="form-label" style={{ fontSize: 13 }}>{t('Reason (optional)', 'السبب (اختياري)')}</label>
              <input className="form-input" value={refundComment} onChange={(e) => setRefundComment(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={submitRefund} disabled={submitting}>
                {submitting ? t('Submitting…', 'جارٍ الإرسال…') : t('Refund', 'استرجاع')}
              </button>
              <button className="btn btn-white" onClick={() => setRefunding(null)} disabled={submitting}>
                {t('Cancel', 'إلغاء')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
